import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ShortUrl } from "@air/api";
import { tailwindMerge } from "@air/tailwind-variants";
import { ClipUtils } from "@air/utils-clips";
import isEqual from "lodash/isEqual";
import { memo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BoardCollectionGalleryAssetCustomFields } from "~/components/BoardCollection/BoardCollectionGalleryAssetCustomFields";
import { BoardCollectionGalleryAssetDescription } from "~/components/BoardCollection/BoardCollectionGalleryAssetDescription";
import { BoardCollectionGalleryAssetMenu } from "~/components/BoardCollection/BoardCollectionGalleryAssetMenu";
import { BoardCollectionGalleryAssetThumbnail } from "~/components/BoardCollection/BoardCollectionGalleryAssetThumbnail";
import { BoardCollectionGalleryAssetThumbnailMeta } from "~/components/BoardCollection/BoardCollectionGalleryAssetThumbnailMeta";
import { BoardCollectionGalleryAssetTitle } from "~/components/BoardCollection/BoardCollectionGalleryAssetTitle";
import { useGetBoardCollectionGalleryAssetMenuOptions } from "~/components/BoardCollection/useGetBoardCollectionGalleryAssetMenuOptions";
import { GalleryCard } from "~/components/Gallery/GalleryCard/GalleryCard";
import { GalleryItemType } from "~/components/Gallery/types";
import { BOARD_COLLECTION_ASSET_CARD } from "~/constants/testIDs";
import { useClipStatusChecker } from "~/hooks/useClipStatusChecker";
import { updateBoardCollectionClipsAction } from "~/store/boardCollection/actions";
import { boardCollectionFormIsInvalidSelector, boardCollectionShortIdSelector } from "~/store/boardCollection/selectors";
export const BoardCollectionGalleryAsset = /*#__PURE__*/ memo((param)=>{
    let { data: clip, box } = param;
    const dispatch = useDispatch();
    const showInvalidState = useSelector((st)=>boardCollectionFormIsInvalidSelector(st, [
            clip.id
        ]));
    const shortId = useSelector(boardCollectionShortIdSelector);
    const { data: updatedClip } = useClipStatusChecker({
        ...clip,
        fileSize: clip.size,
        fetchDetails: async ()=>(await ShortUrl.getBoardClip(shortId, clip.id)).clip
    });
    useEffect(()=>{
        if (updatedClip && updatedClip.status !== clip.status && ClipUtils.isTranscodeFinished(updatedClip.status)) {
            dispatch(updateBoardCollectionClipsAction({
                clips: [
                    {
                        ...updatedClip,
                        // we re-assign these values so the server doesn't overwrite any local values set by the user
                        // after the clip has transcoded
                        title: clip.title,
                        description: clip.description,
                        customFields: clip.customFields
                    }
                ]
            }));
        }
    }, [
        clip.customFields,
        clip.description,
        clip.status,
        clip.title,
        dispatch,
        updatedClip
    ]);
    const { getBoardCollectionAssetMenuOptions } = useGetBoardCollectionGalleryAssetMenuOptions({
        clip
    });
    return /*#__PURE__*/ _jsx(GalleryCard, {
        testId: "".concat(BOARD_COLLECTION_ASSET_CARD, "-").concat(clip.title),
        title: clip.title || clip.id,
        item: {
            id: clip.id,
            item: clip,
            type: GalleryItemType.asset
        },
        getItemMenuOptions: getBoardCollectionAssetMenuOptions,
        getSelectionMenuOptions: getBoardCollectionAssetMenuOptions,
        isSelectable: true,
        getButtonClassName: (param)=>{
            let { isSelected } = param;
            return tailwindMerge("inset-[0] bg-white p-2", showInvalidState && "border-flamingo-500", isSelected && "border-macaw-300");
        },
        onClick: undefined,
        children: (param)=>{
            let { isHovering, setIsMenuOpen } = param;
            return /*#__PURE__*/ _jsxs("div", {
                className: "pointer-events-none flex h-full flex-col rounded p-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "relative flex grow overflow-hidden rounded",
                        children: [
                            /*#__PURE__*/ _jsx(BoardCollectionGalleryAssetThumbnail, {
                                clip: clip,
                                width: box.width,
                                height: box.height
                            }),
                            /*#__PURE__*/ _jsx(BoardCollectionGalleryAssetThumbnailMeta, {
                                clip: clip
                            }),
                            isHovering && /*#__PURE__*/ _jsx("div", {
                                className: "pointer-events-auto absolute right-2 top-2",
                                children: /*#__PURE__*/ _jsx(BoardCollectionGalleryAssetMenu, {
                                    clip: clip,
                                    setIsMenuOpen: setIsMenuOpen
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "pointer-events-auto relative",
                        children: [
                            /*#__PURE__*/ _jsx(BoardCollectionGalleryAssetTitle, {
                                clip: clip
                            }),
                            /*#__PURE__*/ _jsx(BoardCollectionGalleryAssetDescription, {
                                clip: clip
                            }),
                            /*#__PURE__*/ _jsx(BoardCollectionGalleryAssetCustomFields, {
                                clip: clip,
                                width: box.width
                            })
                        ]
                    })
                ]
            });
        }
    });
}, isEqual);
BoardCollectionGalleryAsset.displayName = "BoardCollectionGalleryAsset";
